import { cn } from "@/utilities/ui";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        clear: "",
        default: "h-10 px-4 py-2",
        icon: "h-10 w-10",
        lg: "h-11 rounded px-8",
        sm: "h-9 rounded px-3",
      },
      variant: {
        default: cn("bg-secondary text-white text-lg hover:bg-primary"),
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ghost: "hover:bg-card hover:text-accent-foreground",
        link: "text-primary items-start justify-start underline-offset-4 hover:underline",
        outline: cn("bg-transparent text-lg underline"),
        secondary: "text-white p-0",
        basic: cn("text-black text-lg p-0 font-bold"),
        basicWhite: cn("text-white p-0 hover:font-bold"),
        inputStyle: cn(
          "bg-white !px-0 !pl-3 !pr-10 text-gray-400 rounded hover:ring-2 hover:ring-primary",
        ),
        gold: cn("text-primary p-0 hover:font-bold"),
      },
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ className, size, variant }))}
      ref={ref}
      {...props}
    />
  );
};

export { Button, buttonVariants };
